// YouTube audio engine — plays a YouTube video's audio through an off-screen YT IFrame player,
// exposing a minimal <audio>-like surface to the player facade.
import { loadYouTubeApi } from './youtube.js';

export function createYtAudioEngine({ onChange, onEnded }) {
  let player = null;          // YT.Player instance
  let ready = false;
  let creating = false;       // guards against double-create during the async API load
  let pendingVideoId = null;  // play requested before the player was ready
  let currentVideoId = null;  // last loaded id (resume vs restart)
  let pollId = 0;
  let volume = 50;

  const emitChange = () => { try { onChange(); } catch (e) { /* one subscriber must not break the poll */ } };
  const startPoll = () => { stopPoll(); pollId = setInterval(emitChange, 100); };
  function stopPoll() { if (pollId) { clearInterval(pollId); pollId = 0; } }

  function ensureHost() {
    let inner = document.getElementById('glz-yt-audio');
    if (inner) return inner;
    const host = document.createElement('div');
    // Off-screen but displayed — display:none would stop playback.
    host.style.cssText = 'position:fixed;bottom:-200px;left:0;width:320px;height:180px;pointer-events:none;';
    inner = document.createElement('div');
    inner.id = 'glz-yt-audio';
    host.appendChild(inner);
    document.body.appendChild(host);
    return inner;
  }

  function create() {
    if (player || creating) return;
    creating = true;
    const inner = ensureHost();
    loadYouTubeApi().then(() => {
      // eslint-disable-next-line no-undef
      player = new YT.Player(inner, {
        height: '180', width: '320',
        playerVars: { autoplay: 0, controls: 1 },
        events: {
          onReady: () => {
            ready = true;
            player.setVolume(volume);
            if (pendingVideoId) { const id = pendingVideoId; pendingVideoId = null; currentVideoId = id; player.loadVideoById(id); }
          },
          onStateChange: e => {
            if (e.data === 1) { startPoll(); emitChange(); }                 // playing
            else if (e.data === 0) { stopPoll(); try { onEnded(); } catch (err) {} } // ended
            else if (e.data === 2) { stopPoll(); emitChange(); }             // paused
          }
        }
      });
    }).catch(() => { creating = false; /* YT API blocked: player stays null; facade degrades to a no-op, retry on next play */ });
  }

  return {
    warm() { create(); },
    play(videoId) {
      create();
      if (!videoId) return;
      if (player && ready) {
        if (videoId === currentVideoId && player.getPlayerState && player.getPlayerState() === 2) {
          player.playVideo();            // resume the same paused track
        } else {
          currentVideoId = videoId;
          player.loadVideoById(videoId); // load + autoplay a (new) track
        }
      } else {
        pendingVideoId = videoId;        // play on ready
      }
    },
    pause() { stopPoll(); if (player && ready && player.pauseVideo) player.pauseVideo(); emitChange(); },
    stop() { stopPoll(); if (player && ready && player.stopVideo) player.stopVideo(); },
    isPlaying() { return !!player && ready && typeof player.getPlayerState === 'function' && player.getPlayerState() === 1; },
    getCurrentTime() { return (player && ready && player.getCurrentTime) ? (player.getCurrentTime() || 0) : 0; },
    getDuration() { return (player && ready && player.getDuration) ? (player.getDuration() || 0) : 0; },
    seekTo(sec) { if (player && ready && player.seekTo) player.seekTo(sec, true); },
    setVolume(v) { volume = Math.max(0, Math.min(100, Number(v) || 0)); if (player && ready && player.setVolume) player.setVolume(volume); }
  };
}
